/**
 * Web chat tool-card for the memory WRITE tools (M5.2): all approval-gated
 * server-side:
 *
 * - memoryNodeDelete   → soft-delete a graph node ("forget this").
 * - memoryAccessRevoke → revoke an agent's memory access grant.
 * - forgetMemory       → forget a fact/episode by query.
 *
 * Pending → renders {@link ApprovalBanner}. Completed → a confirmation with the
 * target summary. Web-only; a `.native.tsx` sibling renders iOS/Android. Copy
 * from `settings.memoryGraph.card.*`.
 */
import React, { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '../../icons/MaterialSymbol';
import { CardShell } from './card-shell';
import { ApprovalBanner } from './approval-banner';
import { safeGet } from './types';
import type { ToolCardProps } from './types';

const TITLE_KEY: Record<string, string> = {
  memoryNodeDelete: 'settings.memoryGraph.card.forgetNodeTitle',
  memoryAccessRevoke: 'settings.memoryGraph.card.revokeAccessTitle',
  forgetMemory: 'settings.memoryGraph.card.forgetTitle',
};

const DONE_KEY: Record<string, string> = {
  memoryNodeDelete: 'settings.memoryGraph.card.forgotNode',
  memoryAccessRevoke: 'settings.memoryGraph.card.revokedAccess',
  forgetMemory: 'settings.memoryGraph.card.forgot',
};

const ICON_FOR_TOOL: Record<string, string> = {
  memoryNodeDelete: 'delete',
  memoryAccessRevoke: 'link_off',
  forgetMemory: 'ink_eraser',
};

function targetSummary(toolName: string, args: unknown, result: unknown): string | undefined {
  const get = (...keys: string[]) =>
    safeGet<string>(args, ...keys) ?? safeGet<string>(result, ...keys);
  if (toolName === 'memoryAccessRevoke') {
    return get('agentKey') || get('agent') || get('name');
  }
  if (toolName === 'forgetMemory') {
    return get('query') || get('content') || get('summary');
  }
  return get('label') || get('nodeLabel') || get('name') || get('nodeId') || get('id');
}

export const MemoryActionCard: FC<ToolCardProps> = ({
  toolName,
  args,
  result,
  stage,
  approvalId,
}) => {
  const { t } = useTranslation('common');
  const title = t(TITLE_KEY[toolName] ?? 'settings.memoryGraph.card.forgetTitle');
  const summary = targetSummary(toolName, args, result);

  const summaryBlock = summary ? (
    <div
      style={{
        fontSize: 14,
        lineHeight: '20px',
        color: 'var(--text-primary)',
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}
      dir="auto"
    >
      {summary}
    </div>
  ) : null;

  if (stage === 'pending_approval' && approvalId) {
    return (
      <CardShell>
        <div style={{ padding: '16px 18px 4px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <MaterialSymbol
              name={ICON_FOR_TOOL[toolName] ?? 'delete'}
              size={15}
              style={{ color: 'var(--text-tertiary)' }}
            />
            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-tertiary)' }}>
              {t('settings.memoryGraph.card.actionLabel')}
            </span>
          </div>
          <div style={{ fontSize: 16, fontWeight: 500, color: 'var(--text-primary)' }} dir="auto">
            {title}
          </div>
          {summaryBlock}
        </div>
        <ApprovalBanner
          approvalId={approvalId}
          toolName={toolName}
          description={t('settings.memoryGraph.card.confirm')}
        />
      </CardShell>
    );
  }

  return (
    <CardShell>
      <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <MaterialSymbol name="check_circle" size={16} style={{ color: 'var(--success, #1a7f37)' }} />
          <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-tertiary)' }}>
            {t(DONE_KEY[toolName] ?? 'settings.memoryGraph.card.forgot')}
          </span>
        </div>
        <div style={{ fontSize: 16, fontWeight: 500, color: 'var(--text-primary)' }} dir="auto">
          {title}
        </div>
        {summaryBlock}
      </div>
    </CardShell>
  );
};
